import React, { useState } from 'react';
import DayPicker from 'react-day-picker';
import MaskedInput from 'react-text-mask';
import { Modal, Tooltip } from '@mui/material';
import moment from 'moment';

import { ReactComponent as CalendarTodayIcon } from '../../../../assets/svg/calendar_today.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/svg/info_24px.svg';
import styles from './CustomDatePicker.module.scss';

interface ICustomDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  hint?: string;
}

// Mask for date input
const dateMask = [/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
const CALENDAR_INPUT_FORMAT = 'MM-DD-YYYY';

const CustomDatePicker = ({
  value,
  onChange,
  hint
}: ICustomDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(value) : undefined
  );
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date());

  const handleOpen = () => {
    setSelectedDate(value ? new Date(value) : undefined);
    setDisplayMonth(value ? new Date(value) : new Date());
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDayClick = (day: Date) => {
    setSelectedDate(day);
    setDisplayMonth(day);
  };

  const handleSave = () => {
    if (selectedDate) {
      const dateString = moment(selectedDate).format(CALENDAR_INPUT_FORMAT);
      onChange(dateString);
    }
    handleClose();
  };

  const handleCancel = () => {
    setSelectedDate(value ? new Date(value) : undefined);
    handleClose();
  };

  const handleInputChange = (inputValue: string) => {
    const trimmedValue = inputValue.trim();

    if (trimmedValue.length === 10) { // if full date mm-dd-yyyy
      const momentDate = moment(trimmedValue, CALENDAR_INPUT_FORMAT, true);
      if (momentDate.isValid()) {
        const date = momentDate.toDate();
        setSelectedDate(date);
        setDisplayMonth(date);
      }
    }
  };

  const formatDisplayDate = (dateString: string) => {
    return dateString ? moment(dateString).format(CALENDAR_INPUT_FORMAT) : '';
  };

  const formatInputDate = (date: Date | undefined) => {
    return date ? moment(date).format(CALENDAR_INPUT_FORMAT) : '';
  };

  return (
    <>
      {/* Trigger Button */}
      <Tooltip title="Select date" placement="top">
        <button
          className={styles.triggerButton}
          onClick={handleOpen}
          type="button"
        >
          {value ? (
            <span className={styles.dateText}>{formatDisplayDate(value)}</span>
          ) : (
            <CalendarTodayIcon className={styles.calendarIcon} />
          )}
        </button>
      </Tooltip>

      {/* Modal */}
      <Modal open={isOpen} onClose={handleCancel} className={styles.modal}>
        <div className={styles.modalContent} >
          <div className={styles.dayPickerContainer}>

            {/* Date Input */}
            <MaskedInput
              id="dateInput"
              className={styles.maskedDateInput}
              mask={dateMask}
              guide={false}
              value={formatInputDate(selectedDate)}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="mm-dd-yyyy"
            />

            {/* Calendar */}
            <DayPicker
            fixedWeeks={true}
              selectedDays={selectedDate}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
              onDayClick={handleDayClick}
              showOutsideDays
              modifiers={{
                today: new Date(),
              }}
              modifiersStyles={{
                today: {
                  color: '#FF7C02',
                  fontWeight: 'bold',
                  backgroundColor: '#FFEFE6',
                },
                outside: {
                  color: '#ccc',
                },
                selected: {
                  backgroundColor: '#FF7C02',
                  color: 'white',
                },
              }}
            />

            {/* Hint Message */}
            {hint && (
              <div className={styles.hintMessage}>
                <div className={styles.hintIcon}><InfoIcon /></div>
                <span className={styles.hintText}>{hint}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.dayPickerFooter}>
              <button
                className={styles.cancelButton}
                onClick={handleCancel}
              >
                Cancel
              </button>

              <button
                className={styles.okButton}
                onClick={handleSave}
                disabled={!selectedDate || !moment(selectedDate).isValid()}
              >
                Select
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CustomDatePicker;